import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { TimeAgo } from "components/TimeAgo/TimeAgo";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { getRelativeUrl } from "utils/service";
import { WidgetComponentProps } from "../types";

interface LastExecution {
    id: string;
    created_at: string;
}

export interface ActionButtonWidgetConfig {
    /** Id of the flow to run when the button is pressed. */
    flow_id: string;
    /** Label shown on the button. Defaults to "Run". */
    label?: string;
    /** When true, navigate to the created execution after triggering. Defaults to false. */
    navigate_to_execution?: boolean;
    /** Static input values passed to the flow. */
    input_value?: Record<string, unknown>;
}

/**
 * `action_button` widget — a button that triggers a flow execution when pressed.
 *
 * Posts directly to the REST `execution` endpoint (same API that the "Run" action
 * on flow detail pages uses, just without Redux). On success it shows a notification
 * and optionally navigates to the created execution. When navigation is disabled,
 * shows a link to open the latest execution in a new tab together with its age.
 */
export const ActionButtonWidget = ({
    config,
}: WidgetComponentProps<ActionButtonWidgetConfig>) => {
    const [isRunning, setRunning] = useState(false);
    const [lastExecution, setLastExecution] = useState<LastExecution | null>(null);
    const history = useHistory();

    const flowId = config?.flow_id;
    const label = config?.label ?? "Run";
    const navigateAfter = config?.navigate_to_execution ?? false;
    const inputValue = config?.input_value ?? {};

    const handleClick = async () => {
        if (!flowId) {
            NotificationService.error("action_button widget: flow_id is not configured.");
            return;
        }
        setRunning(true);
        try {
            const response = await restClient.post("execution", {
                type: "FLOW",
                flow_id: flowId,
                input_value: inputValue,
                is_productive: true,
                use_breakpoints: false,
            });
            const executionId = response?.data?.id;
            if (navigateAfter && executionId) {
                NotificationService.success(
                    `Started execution${executionId ? ` ${executionId.slice(0, 8)}…` : ""}.`
                );
                history.push(getRelativeUrl("execution", executionId));
            } else if (executionId) {
                setLastExecution({
                    id: executionId,
                    created_at: response?.data?.created_at ?? new Date().toISOString(),
                });
            } else {
                NotificationService.success("Started execution.");
            }
        } catch (err: any) {
            NotificationService.error(<RestErrorNotification error={err} />);
        } finally {
            setRunning(false);
        }
    };

    if (!flowId) {
        return <div className="DashboardWidget__error">flow_id is not configured.</div>;
    }

    const executionLink = lastExecution
        ? getRelativeUrl("execution", lastExecution.id)
        : null;

    return (
        <div className="DashboardActionButton">
            <Button
                variation="primary"
                label={label}
                icon={IconCollection.play}
                loading={isRunning}
                disabled={isRunning}
                onClick={handleClick}
            />
            {!navigateAfter && lastExecution && executionLink && (
                <div className="DashboardActionButton__result">
                    <TimeAgo
                        className="DashboardActionButton__time"
                        datetime={lastExecution.created_at}
                    />
                    <a
                        href={executionLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="DashboardActionButton__link"
                    >
                        Open execution
                    </a>
                </div>
            )}
        </div>
    );
};
